import { useEffect, useState, type FormEvent } from "react";
import { PASSWORD_MIN_LENGTH } from "../../../shared/password-rules";
import { LangToggle } from "../../components/LangToggle";
import { PasswordStrength } from "../../components/PasswordStrength";
import { Button, Card, Input } from "../../components/ui";
import { useT } from "../../i18n";
import { passwordErrorText } from "../../i18n/errors";
import { authClient } from "../../lib/auth";
import { usePasswordCheck } from "../../lib/password";

type Msg = { ok: boolean; text: string } | null;

function MsgLine({ msg }: { msg: Msg }) {
  if (!msg) return null;
  return (
    <p
      className={`mt-2 text-xs ${
        msg.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
      }`}
    >
      {msg.text}
    </p>
  );
}

/** Persönliches Konto: Name, Passwort, E-Mail, 2FA, Sprache (früher Konto-Dialog). */
export function ProfileSettings() {
  const t = useT();
  const { data: session } = authClient.useSession();
  const email = session?.user.email ?? "";
  const sessionName = session?.user.name ?? "";
  const twoFactorEnabled = Boolean(
    (session?.user as { twoFactorEnabled?: boolean } | undefined)?.twoFactorEnabled,
  );

  // null = noch nicht angefasst → Session-Wert anzeigen
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState<Msg>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState<Msg>(null);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<Msg>(null);
  const pwCheck = usePasswordCheck(newPw, { email, name: sessionName });
  // 2FA: lokaler Zustand, weil die Session erst beim nächsten Laden nachzieht
  const [tfaEnabled, setTfaEnabled] = useState(twoFactorEnabled);
  const [tfaPw, setTfaPw] = useState("");
  const [tfaBusy, setTfaBusy] = useState(false);
  const [tfaMsg, setTfaMsg] = useState<Msg>(null);

  useEffect(() => setTfaEnabled(twoFactorEnabled), [twoFactorEnabled]);

  async function submitName(e: FormEvent) {
    e.preventDefault();
    if (nameDraft === null || nameDraft.trim() === "" || nameDraft === sessionName) return;
    setNameBusy(true);
    setNameMsg(null);
    const { error } = await authClient.updateUser({ name: nameDraft.trim() });
    setNameMsg(
      error ? { ok: false, text: t.common.error } : { ok: true, text: t.account.nameChanged },
    );
    setNameBusy(false);
  }

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    setEmailBusy(true);
    setEmailMsg(null);
    const { error } = await authClient.changeEmail({ newEmail });
    if (error) {
      setEmailMsg({ ok: false, text: t.common.error });
    } else {
      // better-auth antwortet bei bereits vergebener Adresse bewusst mit 200,
      // ohne etwas zu ändern (Anti-Enumeration) — deshalb gegen die Session prüfen.
      const session = await authClient.getSession({ query: { disableCookieCache: true } });
      if (session.data?.user.email === newEmail.trim().toLowerCase()) {
        setEmailMsg({ ok: true, text: t.account.emailChanged });
        setNewEmail("");
      } else {
        setEmailMsg({ ok: false, text: t.account.emailTaken });
      }
    }
    setEmailBusy(false);
  }

  async function submitTfa(e: FormEvent) {
    e.preventDefault();
    setTfaBusy(true);
    setTfaMsg(null);
    const { error } = tfaEnabled
      ? await authClient.twoFactor.disable({ password: tfaPw })
      : await authClient.twoFactor.enable({ password: tfaPw, method: "otp" });
    if (error) {
      setTfaMsg({
        ok: false,
        text: error.code === "INVALID_PASSWORD" ? t.account.wrongPassword : t.common.error,
      });
    } else {
      const nowEnabled = !tfaEnabled;
      setTfaEnabled(nowEnabled);
      setTfaMsg({ ok: true, text: nowEnabled ? t.twofa.enabledMsg : t.twofa.disabledMsg });
      setTfaPw("");
      // Session-Cookie wurde rotiert — Stand nachladen (Header/App)
      void authClient.getSession({ query: { disableCookieCache: true } });
    }
    setTfaBusy(false);
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setPwBusy(true);
    setPwMsg(null);
    const { error } = await authClient.changePassword({
      currentPassword: currentPw,
      newPassword: newPw,
      // Sicherheitsnetz: andere Geräte müssen sich neu anmelden
      revokeOtherSessions: true,
    });
    if (error) {
      setPwMsg({
        ok: false,
        text:
          error.code === "INVALID_PASSWORD"
            ? t.account.wrongPassword
            : (passwordErrorText(t, error.code) ?? t.common.error),
      });
    } else {
      setPwMsg({ ok: true, text: t.account.passwordChanged });
      setCurrentPw("");
      setNewPw("");
    }
    setPwBusy(false);
  }

  return (
    <Card className="max-w-xl">
      <section>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.account.nameSection}
        </h4>
        <form onSubmit={(e) => void submitName(e)} className="flex gap-2">
          <Input
            className="min-w-0 flex-1"
            required
            value={nameDraft ?? sessionName}
            onChange={(e) => setNameDraft(e.target.value)}
          />
          <Button type="submit" variant="ghost" disabled={nameBusy}>
            {t.account.change}
          </Button>
        </form>
        <MsgLine msg={nameMsg} />
      </section>

      <section className="mt-5 border-t border-zinc-100 pt-5 dark:border-zinc-800">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.account.passwordSection}
        </h4>
        <form onSubmit={(e) => void submitPassword(e)} className="space-y-2">
          <Input
            className="w-full"
            type="password"
            required
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            placeholder={t.account.currentPassword}
          />
          <Input
            className="w-full"
            type="password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder={t.account.newPassword(PASSWORD_MIN_LENGTH)}
          />
          <PasswordStrength password={newPw} result={pwCheck} />
          <div className="flex justify-end">
            <Button type="submit" variant="ghost" disabled={pwBusy || !!pwCheck?.rejection}>
              {t.account.changePassword}
            </Button>
          </div>
        </form>
        <MsgLine msg={pwMsg} />
      </section>

      <section className="mt-5 border-t border-zinc-100 pt-5 dark:border-zinc-800">
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.account.emailSection}
        </h4>
        <p className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">
          {t.account.currentEmail(email)}
        </p>
        <form onSubmit={(e) => void submitEmail(e)} className="flex gap-2">
          <Input
            className="min-w-0 flex-1"
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t.account.newEmailPlaceholder}
          />
          <Button type="submit" variant="ghost" disabled={emailBusy}>
            {t.account.change}
          </Button>
        </form>
        <MsgLine msg={emailMsg} />
      </section>

      <section className="mt-5 border-t border-zinc-100 pt-5 dark:border-zinc-800">
        <div className="mb-1 flex items-center gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
            {t.twofa.section}
          </h4>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              tfaEnabled
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400"
                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
            }`}
          >
            {tfaEnabled ? t.twofa.statusOn : t.twofa.statusOff}
          </span>
        </div>
        <p className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">
          {tfaEnabled ? t.twofa.enabledHint : t.twofa.disabledHint}
        </p>
        <form onSubmit={(e) => void submitTfa(e)} className="flex gap-2">
          <Input
            className="min-w-0 flex-1"
            type="password"
            required
            autoComplete="current-password"
            value={tfaPw}
            onChange={(e) => setTfaPw(e.target.value)}
            placeholder={t.account.currentPassword}
          />
          <Button type="submit" variant="ghost" disabled={tfaBusy}>
            {tfaEnabled ? t.twofa.disable : t.twofa.enable}
          </Button>
        </form>
        <MsgLine msg={tfaMsg} />
      </section>

      <section className="mt-5 border-t border-zinc-100 pt-5 dark:border-zinc-800">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.menu.language}
        </h4>
        <LangToggle />
      </section>
    </Card>
  );
}
